import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Check, X } from "lucide-react";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { adminListTypes, adminCreateType, adminRenameType, adminDeleteType } from "../../services/api";

const input = "border border-brand-sand bg-brand-surface px-4 py-2.5 text-sm text-brand-charcoal outline-none focus:border-brand-orange";

export default function AdminTypes() {
  const { user, ready } = useAuth();
  const [types, setTypes] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const detail = (err, fb) => err?.response?.data?.detail || fb;

  const load = () => adminListTypes().then(setTypes).catch(() => toast.error("Could not load types."));
  useEffect(() => { if (ready && user?.role === "admin") load(); }, [ready, user]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try { await adminCreateType(name.trim()); setName(""); toast.success("Type added."); load(); }
    catch (err) { toast.error(detail(err, "Could not add type.")); }
    finally { setBusy(false); }
  };
  const rename = async (slug) => {
    if (!editName.trim()) return;
    try { await adminRenameType(slug, editName.trim()); setEditing(null); toast.success("Type renamed."); load(); }
    catch (err) { toast.error(detail(err, "Rename failed.")); }
  };
  const remove = async (t) => {
    if (!window.confirm(`Delete type "${t.name}"?`)) return;
    try { await adminDeleteType(t.slug); toast.success("Type deleted."); load(); }
    catch (err) { toast.error(detail(err, "Delete failed.")); }
  };

  return (
    <AdminLayout title="Bracelet Types" subtitle="Owner-managed list used by the product form (e.g. Adjustable, Stretch, Tennis, Cuff)">
      <div data-testid="admin-types-page" className="max-w-3xl">
        <form onSubmit={create} className="flex gap-3 border border-brand-sand bg-brand-surface p-4">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New type name" data-testid="type-name-input" className={`${input} flex-1`} />
          <button type="submit" disabled={busy || !name.trim()} data-testid="type-add-btn" className="flex items-center gap-2 bg-brand-charcoal px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand-cream hover:bg-brand-orange disabled:opacity-50">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>
        <div className="mt-4 border border-brand-sand bg-brand-surface" data-testid="types-table">
          {types.length === 0 ? (
            <p className="p-8 text-center text-sm text-brand-stone" data-testid="types-empty">No types yet. Add your first bracelet type above.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-brand-sand text-[11px] font-semibold uppercase tracking-[0.16em] text-brand-stone"><th className="p-3">Type</th><th className="p-3">Slug</th><th className="p-3">Bracelets</th><th className="p-3 text-right">Actions</th></tr></thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.slug} className="border-b border-brand-sand/50" data-testid={`type-row-${t.slug}`}>
                    <td className="p-3">
                      {editing === t.slug ? (
                        <span className="flex items-center gap-2">
                          <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`type-edit-input-${t.slug}`} className={input} />
                          <button onClick={() => rename(t.slug)} data-testid={`type-edit-save-${t.slug}`} className="text-brand-orange"><Check className="h-4 w-4" /></button>
                          <button onClick={() => setEditing(null)} className="text-brand-stone"><X className="h-4 w-4" /></button>
                        </span>
                      ) : <span className="font-medium text-brand-charcoal">{t.name}</span>}
                    </td>
                    <td className="p-3 text-brand-stone">{t.slug}</td>
                    <td className="p-3 text-brand-stone">{t.product_count}</td>
                    <td className="p-3">
                      <div className="flex items-center justify-end gap-3">
                        <button onClick={() => { setEditing(t.slug); setEditName(t.name); }} data-testid={`type-edit-${t.slug}`} className="text-brand-stone hover:text-brand-orange"><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => remove(t)} data-testid={`type-delete-${t.slug}`} className="text-brand-stone hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
